import { useState, useMemo } from "react";
import { num, toM, ml, m2, money, UNITS, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, ResultCard, useCatalog, ProductSelect } from "./shared";

const INSTAL_DESP = { "Recta": 5, "Con cortes": 8, "Irregular": 12, "Diagonal": 15, "Espiga": 18, "Escalera": 12 };

export default function Deck({ onSave, s }) {
  const items = useCatalog("deck");
  const [pid, setPid] = useState("");
  const prod = items.find((p) => p.id === pid);
  const [u, setU] = useState("m");
  const [tipo, setTipo] = useState("WPC");
  const [largo, setLargo] = useState("6");
  const [ancho, setAncho] = useState("4");
  const [sentido, setSentido] = useState("largo");
  const [tablaAncho, setTablaAncho] = useState("14");
  const [tablaSep, setTablaSep] = useState("0.5");
  const [tablaLargo, setTablaLargo] = useState("2.50");
  const [instal, setInstal] = useState("Con cortes");
  const [sepApoyos, setSepApoyos] = useState("40");
  const [bastidorLargo, setBastidorLargo] = useState("3");
  const [despMode, setDespMode] = useState("auto");
  const [despManual, setDespManual] = useState("8");
  const [precioTabla, setPrecioTabla] = useState("");
  const [precioInst, setPrecioInst] = useState("");
  const despPct = despMode === "auto" ? (INSTAL_DESP[instal] ?? 8) : num(despManual);
  const pr = prod ? prod.price : num(precioTabla);

  const r = useMemo(() => {
    const L = toM(largo, u), W = toM(ancho, u);
    const area = L * W;
    const anchoUtil = (num(tablaAncho) + num(tablaSep)) / 100; // m
    const perpTablas = sentido === "largo" ? W : L; // perpendicular to board direction
    const largoHilera = sentido === "largo" ? L : W;
    const hileras = anchoUtil > 0 ? Math.ceil(perpTablas / anchoUtil) : 0;
    const mlTeorico = hileras * largoHilera;
    const mlAjust = mlTeorico * (1 + despPct / 100);
    const tablas = num(tablaLargo) > 0 ? Math.ceil(mlAjust / num(tablaLargo)) : 0;
    // Estructura (bastidores)
    const dimParalela = sentido === "largo" ? L : W;
    const dimPerpLineas = sentido === "largo" ? W : L;
    const lineas = num(sepApoyos) > 0 ? Math.ceil(dimParalela / (num(sepApoyos) / 100)) + 2 : 0;
    const mlBastidor = lineas * dimPerpLineas * 1.05;
    const piezasBastidor = num(bastidorLargo) > 0 ? Math.ceil(mlBastidor / num(bastidorLargo)) : 0;
    const clips = hileras * lineas;
    const tornillos = clips * 2;
    const perim = 2 * (L + W);
    const cMat = tablas * pr;
    const total = cMat + num(precioInst);
    const w = [];
    if (area === 0) w.push("Captura largo y ancho del área.");
    if (prod && prod.stock < tablas) w.push(`Inventario insuficiente de "${prod.name}": stock ${prod.stock}, requieres ${tablas} tablas.`);
    const rows = [
      { section: "Proyecto" },
      { label: "Producto", value: tipo }, { label: "Área", value: m2(area) }, { label: "Sentido de instalación", value: sentido === "largo" ? "Sobre el largo" : "Sobre el ancho" },
      { label: "Instalación", value: instal },
      { section: "Deck" },
      { label: "Ancho útil instalado", value: `${(anchoUtil * 100).toFixed(1)} cm` }, { label: "Número de hileras", value: `${hileras}` },
      { label: "Metros lineales teóricos", value: ml(mlTeorico) }, { label: "Desperdicio aplicado", value: `${despPct}%` },
      { label: "Tablas a vender", value: `${tablas} tabla(s)`, strong: true },
      { section: "Estructura" },
      { label: "Líneas de bastidor", value: `${lineas}` }, { label: "Metros lineales de bastidor", value: ml(mlBastidor) },
      { label: "Piezas de bastidor", value: `${piezasBastidor}` },
      { section: "Fijación / remates" },
      { label: "Clips", value: `${clips}` }, { label: "Tornillos (aprox.)", value: `${tornillos}` },
      { label: "Perfiles / fascia (perímetro)", value: ml(perim) },
      { section: "Costos" },
      { label: "Materiales (tablas)", value: money(cMat) }, { label: "Instalación", value: money(num(precioInst)) },
      { label: "Total estimado", value: money(total), strong: true },
    ];
    return { rows, warnings: w, total, inputs: { u, tipo, sentido } };
  }, [u, tipo, largo, ancho, sentido, tablaAncho, tablaSep, tablaLargo, instal, sepApoyos, bastidorLargo, despPct, pr, precioInst, prod]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo calcular deck / madera tecnológica?"><p>Se calculan hileras según el ancho útil de tabla (ancho + separación), los metros lineales, las tablas comerciales, la estructura de bastidores, clips, tornillos y remates perimetrales.</p></HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={UNITS} testid="dk-unidad" />
          <Sel label="Producto" value={tipo} onChange={setTipo} opts={["WPC", "Madera natural", "Encapsulado", "Madera tecnológica", "Bambú"]} testid="dk-tipo" />
          <Field label="Largo del área" value={largo} onChange={setLargo} suffix={u} testid="dk-largo" />
          <Field label="Ancho del área" value={ancho} onChange={setAncho} suffix={u} testid="dk-ancho" />
          <Sel label="Sentido de instalación" value={sentido} onChange={setSentido} opts={[["largo", "Sobre el largo"], ["ancho", "Sobre el ancho"]]} testid="dk-sentido" />
          <Sel label="Instalación" value={instal} onChange={setInstal} opts={Object.keys(INSTAL_DESP)} testid="dk-instal" />
        </Card>
        <ProductSelect items={items} value={pid} onChange={setPid} testid="dk-product" />
        <Card>
          <Field label="Ancho real de tabla (cm)" value={tablaAncho} onChange={setTablaAncho} step="0.5" suffix="cm" testid="dk-tablaancho" />
          <Field label="Separación entre tablas (cm)" value={tablaSep} onChange={setTablaSep} step="0.1" suffix="cm" testid="dk-tablasep" />
          <Field label="Largo comercial de tabla (m)" value={tablaLargo} onChange={setTablaLargo} step="0.1" suffix="m" testid="dk-tablalargo" />
          <Field label="Separación entre apoyos (cm)" value={sepApoyos} onChange={setSepApoyos} step="5" suffix="cm" testid="dk-sepapoyos" />
        </Card>
        <Advanced>
          <Sel label="Desperdicio" value={despMode} onChange={setDespMode} opts={[["auto", `Automático (${INSTAL_DESP[instal] ?? 8}%)`], ["manual", "Manual"]]} testid="dk-despmode" />
          {despMode === "manual" && <Field label="Desperdicio (%)" value={despManual} onChange={setDespManual} step="1" suffix="%" testid="dk-despmanual" />}
          <Field label="Largo comercial de bastidor (m)" value={bastidorLargo} onChange={setBastidorLargo} step="0.1" suffix="m" testid="dk-bastidorlargo" />
          <Field label="Precio por tabla ($)" value={prod ? prod.price : precioTabla} onChange={setPrecioTabla} suffix="$" testid="dk-precio" />
          <Field label="Instalación ($)" value={precioInst} onChange={setPrecioInst} suffix="$" testid="dk-precio-inst" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Deck — GOÛT" modality="deck" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave} />
    </CalcLayout>
  );
}
